import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Form, Select, DatePicker, Button, Table, Space, message, Spin } from 'antd'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { fetchCompareData, selectCompare } from '../../store/analyticsSlice'
import apiService from '../../services/api'
import dayjs from 'dayjs'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const { RangePicker } = DatePicker
const { Option } = Select

const CompareChart = () => {
  const dispatch = useDispatch()
  const { data, loading, error } = useSelector(selectCompare)
  const [form] = Form.useForm()

  const [exchanges, setExchanges] = useState([])
  const [accounts, setAccounts] = useState([])
  const [pairs, setPairs] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  useEffect(() => {
    loadOptions()
  }, [])

  const loadOptions = async () => {
    setLoadingOptions(true)
    try {
      const [exchangesRes, accountsRes] = await Promise.all([
        apiService.exchanges.getActiveExchanges(),
        apiService.trades.getActiveAccounts(),
      ])

      setExchanges(exchangesRes.data.data || [])
      setAccounts(accountsRes.data.data || [])
    } catch (err) {
      message.error('加载选项失败')
    } finally {
      setLoadingOptions(false)
    }
  }

  const handleSubmit = async (values) => {
    const params = {
      dimension: values.dimension === 'date' ? 'time' : values.dimension,
      metrics: [values.metric],
      start_date: values.dateRange[0].format('YYYY-MM-DD'),
      end_date: values.dateRange[1].format('YYYY-MM-DD'),
      compare_type: 'target_comparison',
    }

    // 设置目标
    if (values.dimension === 'exchange' && values.exchanges?.length) {
      params.targets = values.exchanges
    } else if (values.dimension === 'account' && values.accounts?.length) {
      params.targets = values.accounts
    } else if (values.dimension === 'pair' && values.pairs?.length) {
      params.targets = values.pairs
    } else {
      // 如果没有选择具体目标，则将 dimensions 本身作为 targets（或者由后端处理）
      // 这里为了简单，如果没选则留空，后端可能会返回该维度的所有数据
      params.targets = []
    }

    dispatch(fetchCompareData(params))
  }

  const chartData = data ? {
    labels: data.labels || [],
    datasets: (data.datasets || []).map((ds, idx) => ({
      label: ds.label,
      data: ds.data,
      borderColor: `hsl(${idx * 60}, 70%, 50%)`,
      backgroundColor: `hsla(${idx * 60}, 70%, 50%, 0.1)`,
      tension: 0.3,
    })),
  } : null

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '对比分析图表',
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const columns = [
    {
      title: '维度',
      dataIndex: 'dimension',
      key: 'dimension',
    },
    {
      title: '指标值',
      dataIndex: 'value',
      key: 'value',
      render: (val) => typeof val === 'number' ? val.toFixed(2) : val,
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (val) => val ? `${val.toFixed(2)}%` : '-',
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Card title="对比分析" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleSubmit}
          initialValues={{
            dimension: 'exchange',
            metric: 'volume',
            dateRange: [
              dayjs().subtract(7, 'day').startOf('day'),
              dayjs().subtract(1, 'day').endOf('day'),
            ],
          }}
        >
          <Form.Item name="dimension" label="对比维度" rules={[{ required: true }]}>
            <Select style={{ width: 150 }}>
              <Option key="exchange" value="exchange">交易所</Option>
              <Option key="account" value="account">账户</Option>
              <Option key="pair" value="pair">交易对</Option>
              <Option key="date" value="date">日期</Option>
            </Select>
          </Form.Item>

          <Form.Item name="metric" label="指标" rules={[{ required: true }]}>
            <Select style={{ width: 150 }}>
              <Option key="volume" value="volume">交易量</Option>
              <Option key="pnl" value="pnl">盈亏</Option>
              <Option key="maker_volume" value="maker_volume">Maker 交易量</Option>
              <Option key="taker_volume" value="taker_volume">Taker 交易量</Option>
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="日期范围" rules={[{ required: true }]}>
            <RangePicker />
          </Form.Item>

          <Form.Item name="exchanges" label="交易所">
            <Select
              mode="multiple"
              style={{ width: 200 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {exchanges.map(ex => (
                <Option key={ex.code} value={ex.code}>{ex.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="accounts" label="账户">
            <Select
              mode="multiple"
              style={{ width: 200 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {accounts.map(acc => (
                <Option key={`${acc.account}_${acc.exchange}_${acc.trade_type}`} value={acc.account}>{acc.account}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {error && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ color: 'red' }}>错误: {error}</div>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        </Card>
      )}

      {data && !loading && (
        <>
          <Card title="图表展示" style={{ marginBottom: 24 }}>
            <div style={{ height: 400 }}>
              {chartData && <Line data={chartData} options={chartOptions} />}
            </div>
          </Card>

          {data.table && (
            <Card title="数据表格">
              <Table
                columns={columns}
                dataSource={data.table}
                rowKey={(record, idx) => idx}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default CompareChart
